import { ar, en } from '@dob/i18n';
import {
  LONG_PASSWORD_REQUIRED,
  PASSWORD_MAX_LENGTH,
  passwordLength,
  passwordRule,
  type LocalizedText,
  type PasswordRule,
} from '@dob/domain';
import { useState } from 'react';

import { Alert, TextInput } from '../../components/ui.tsx';
import type { useFormErrors } from '../../components/useFormErrors.ts';
import { checkLength } from '../../forms.ts';
import { useLang } from '../../i18n.ts';

/** When the person's rule is not known (password reset before sign-in): the general one. */
const GENERAL_RULE = passwordRule(false);

const PASSWORDS_DIFFER: LocalizedText = {
  ar: ar.auth.passwordsDiffer,
  en: en.auth.passwordsDiffer,
};

/**
 * The new-password pair. `rule` is the person's own (profile `passwordRule`): 8 characters, or
 * 16 for a management account that can sign in without internet (owner rule D147). The server
 * applies the same rule and also refuses common passwords.
 */
export function useNewPassword(rule: PasswordRule = GENERAL_RULE) {
  const [value, setValue] = useState('');
  const [repeat, setRepeat] = useState('');
  return {
    value,
    setValue,
    repeat,
    setRepeat,
    /** Client checks for the two fields (same words as the server's). */
    checks(): Record<string, LocalizedText | undefined> {
      const tooShort = value !== '' && passwordLength(value) < rule.minLength;
      return {
        newPassword:
          tooShort && rule.reason === 'offline_management'
            ? LONG_PASSWORD_REQUIRED
            : value === '' || tooShort || passwordLength(value) > PASSWORD_MAX_LENGTH
              ? checkLength(value, rule.minLength, PASSWORD_MAX_LENGTH)
              : undefined,
        repeatPassword:
          repeat === '' ? checkLength(repeat, 1) : repeat === value ? undefined : PASSWORDS_DIFFER,
      };
    },
  };
}

export function NewPasswordFields({
  state,
  errors,
  rule = GENERAL_RULE,
}: {
  readonly state: ReturnType<typeof useNewPassword>;
  readonly errors: ReturnType<typeof useFormErrors>;
  readonly rule?: PasswordRule | undefined;
}) {
  const { t } = useLang();
  const long = rule.reason === 'offline_management';
  return (
    <>
      {long ? (
        <Alert tone="info" title={t('auth.longPasswordTitle')}>
          {t('auth.longPasswordBody')}
        </Alert>
      ) : null}
      <TextInput
        label={t('auth.newPassword')}
        hint={long ? t('auth.passwordRulesLong') : t('auth.passwordRules')}
        type="password"
        autoComplete="new-password"
        dir="ltr"
        value={state.value}
        error={errors.errorFor('newPassword')}
        onChange={(e) => {
          errors.touched('newPassword');
          state.setValue(e.target.value);
        }}
        className="text-start"
      />
      <TextInput
        label={t('auth.repeatPassword')}
        type="password"
        autoComplete="new-password"
        dir="ltr"
        value={state.repeat}
        error={errors.errorFor('repeatPassword')}
        onChange={(e) => {
          errors.touched('repeatPassword');
          state.setRepeat(e.target.value);
        }}
        className="text-start"
      />
    </>
  );
}
